import React from 'react';
import { Edit3, Trash2, ChevronDown, ChevronUp, ShoppingBag, Send, RotateCcw, AlertTriangle, Recycle, History, UserCheck, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { STATUS_STYLES, CONDITION_COLORS, formatDate } from './constants';
import BoardForm from './BoardForm';
import PhotoGallery from './PhotoGallery';
import PhotoUpload from './PhotoUpload';
import ProvenanceChain from './ProvenanceChain';

export default function BoardCard({
  board, index,
  isExpanded, isEditing, isHistoryOpen,
  onToggleExpand, onEdit, onCancelEdit, onDelete, onUpdate,
  onReturn, onTransfer, onStatusChange, onToggleHistory,
  onPhotoUploaded,
  brandNames, lengthOpts, volumeOpts, finOpts, onAddBrand,
}) {
  const condStyle = CONDITION_COLORS[board.condition] || CONDITION_COLORS['Good'];
  const boardStatus = board.status || 'active';
  const statusCfg = STATUS_STYLES[boardStatus] || STATUS_STYLES.active;
  const isActive = boardStatus === 'active';
  const isLent = boardStatus === 'lent';
  const isInactive = ['sold', 'lost', 'recycled', 'deleted', 'gifted', 'found'].includes(boardStatus);

  return (
    <motion.div
      key={board.board_id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: index * 0.04 }}
      className={`bg-bg-subtle/50 border rounded-2xl overflow-hidden ${isInactive ? 'border-white/5 opacity-70' : 'border-white/5'}`}
      data-testid={`board-${board.board_id}`}
    >
      {/* Card header */}
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center gap-3 p-4 text-left"
        data-testid={`board-toggle-${board.board_id}`}
      >
        <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0 relative">
          <span className="text-lg">🏄</span>
          <div className={`absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-bg-subtle ${boardStatus === 'active' ? 'bg-green-400' : boardStatus === 'lent' ? 'bg-yellow-400' : boardStatus === 'sold' ? 'bg-blue-400' : boardStatus === 'lost' ? 'bg-red-400' : 'bg-purple-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-sm font-medium truncate ${isInactive ? 'line-through text-muted-foreground' : ''}`}>{board.name}</p>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full border flex-shrink-0 ${statusCfg.color}`}>{statusCfg.label}</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {[board.board_type, board.length, board.brand].filter(Boolean).join(' · ') || 'no details yet'}
          </p>
          <p className="text-[9px] text-muted-foreground/40 font-mono mt-0.5">{board.serial || board.board_id}</p>
        </div>
        <div className="text-right flex-shrink-0 mr-1">
          <p className="text-xs font-bold text-muted-foreground">{board.session_count || 0}</p>
          <p className="text-[9px] text-muted-foreground/60">sessions</p>
        </div>
        {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />}
      </button>

      {/* Expanded detail */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4 border-t border-white/5 pt-3">
              {isEditing ? (
                <BoardForm
                  initial={{ name: board.name, board_type: board.board_type, length: board.length, volume: board.volume, fins: board.fins, brand: board.brand, condition: board.condition, notes: board.notes }}
                  onSave={(form) => onUpdate(board.board_id, form)}
                  onCancel={onCancelEdit}
                  brands={brandNames} lengths={lengthOpts} volumes={volumeOpts} fins={finOpts} onAddBrand={onAddBrand}
                />
              ) : (
                <>
                  {/* Top-right actions: Edit + Delete */}
                  <div className="flex items-center justify-end gap-1.5 mb-2">
                    {!isInactive && (
                      <button
                        data-testid={`edit-board-${board.board_id}`}
                        onClick={() => onEdit(board.board_id)}
                        className="text-[10px] px-2.5 py-1 rounded-full bg-white/5 text-muted-foreground hover:text-white transition-colors inline-flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" /> Edit
                      </button>
                    )}
                    <button
                      data-testid={`delete-board-${board.board_id}`}
                      onClick={() => onDelete(board.board_id)}
                      className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </div>

                  {/* Details grid */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-3">
                    {board.volume && <div><span className="text-muted-foreground/60">Volume:</span> <span>{board.volume}</span></div>}
                    {board.fins && <div><span className="text-muted-foreground/60">Fins:</span> <span>{board.fins}</span></div>}
                    {board.brand && <div><span className="text-muted-foreground/60">Brand:</span> <span>{board.brand}</span></div>}
                    {board.length && <div><span className="text-muted-foreground/60">Length:</span> <span>{board.length}</span></div>}
                    <div><span className="text-muted-foreground/60">Condition:</span> <span className={condStyle.split(' ')[0]}> {board.condition}</span></div>
                    {isLent && board.lent_to && <div><span className="text-muted-foreground/60">Lent to:</span> <span className="text-yellow-400">@{board.lent_to}</span></div>}
                    {boardStatus === 'lost' && board.lost_spot && <div><span className="text-muted-foreground/60">Lost at:</span> <span className="text-red-400 inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{board.lost_spot}</span></div>}
                    {boardStatus === 'lost' && board.lost_date && <div><span className="text-muted-foreground/60">Lost on:</span> <span className="text-red-400">{formatDate(board.lost_date)}</span></div>}
                  </div>
                  {board.notes && <p className="text-[11px] text-muted-foreground/70 italic mb-3">"{board.notes}"</p>}

                  {/* Photo Gallery */}
                  {board.photos && board.photos.length > 0 && (
                    <div className="mb-3">
                      <PhotoGallery photos={board.photos} boardId={board.board_id} />
                    </div>
                  )}

                  {/* Photo upload (only active/lent boards) */}
                  {!isInactive && (
                    <div className="mb-3">
                      <PhotoUpload boardId={board.board_id} onUploaded={onPhotoUploaded} />
                    </div>
                  )}

                  {/* Lent board: return action */}
                  {isLent && (
                    <button
                      data-testid={`return-board-${board.board_id}`}
                      onClick={() => onReturn(board.board_id)}
                      className="w-full mb-3 bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-semibold py-2 rounded-xl hover:bg-green-500/15 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Mark as Returned
                    </button>
                  )}

                  {/* Action buttons */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {isActive && (
                      <>
                        <button
                          data-testid={`sell-board-${board.board_id}`}
                          onClick={() => onTransfer(board, 'sold')}
                          className="text-[10px] px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/15 transition-colors inline-flex items-center gap-1"
                        >
                          <ShoppingBag className="w-3 h-3" /> Sell
                        </button>
                        <button
                          data-testid={`lend-board-${board.board_id}`}
                          onClick={() => onTransfer(board, 'lent')}
                          className="text-[10px] px-3 py-1.5 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 hover:bg-yellow-500/15 transition-colors inline-flex items-center gap-1"
                        >
                          <Send className="w-3 h-3" /> Lend
                        </button>
                        <button
                          data-testid={`gift-board-${board.board_id}`}
                          onClick={() => onTransfer(board, 'gifted')}
                          className="text-[10px] px-3 py-1.5 rounded-full bg-pink-500/10 text-pink-400 border border-pink-500/20 hover:bg-pink-500/15 transition-colors inline-flex items-center gap-1"
                        >
                          <UserCheck className="w-3 h-3" /> Gift
                        </button>
                      </>
                    )}
                    {(isActive || isLent) && (
                      <>
                        <button
                          data-testid={`lost-board-${board.board_id}`}
                          onClick={() => onStatusChange(board, 'lost')}
                          className="text-[10px] px-3 py-1.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/15 transition-colors inline-flex items-center gap-1"
                        >
                          <AlertTriangle className="w-3 h-3" /> Lost
                        </button>
                        <button
                          data-testid={`recycle-board-${board.board_id}`}
                          onClick={() => onStatusChange(board, 'recycled')}
                          className="text-[10px] px-3 py-1.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/15 transition-colors inline-flex items-center gap-1"
                        >
                          <Recycle className="w-3 h-3" /> Recycle
                        </button>
                      </>
                    )}
                  </div>

                  {/* Provenance toggle */}
                  <button
                    data-testid={`history-toggle-${board.board_id}`}
                    onClick={onToggleHistory}
                    className="w-full flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-xl px-3 py-2 hover:border-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <History className="w-3.5 h-3.5 text-primary/50" />
                      <span className="text-[10px] text-muted-foreground">Provenance Chain</span>
                      <span className="text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{(board.history || []).length} events</span>
                    </div>
                    {isHistoryOpen ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground/40" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/40" />}
                  </button>

                  {/* History chain */}
                  <AnimatePresence>
                    {isHistoryOpen && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                        <ProvenanceChain history={board.history || []} serial={board.serial || board.board_id} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
